import classnames from 'classnames'
import styles from './ProductGalleryGridView.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import Link from '@/components/Link/Link'
import { formatNumber, getProductLinkObject } from '@/utils'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import gsap from 'gsap'
import { PRODUCT_GALLERY_BREAKPOINTS } from '@/sections/ProductGallery/ProductGallery'
import useStore from '@/store'

const ProductGalleryGridView = forwardRef(({ className, products, colorsByIndex }, ref) => {
  const $container = useRef(null)
  const $items = useRef([])
  const $imageContainers = useRef([])
  const $numberAndTitleContainers = useRef([])
  const productGalleryType = useStore(state => state.productGalleryType)
  const productGalleryActiveFilter = useStore(state => state.productGalleryActiveFilter)

  const getOffsetByPercentScrolled = percent => {
    let activeIndex = gsap.utils.mapRange(0, 1, 0, $items.current.length - 1, percent)
    activeIndex = gsap.utils.snap(1, activeIndex)

    let offset = parseInt($items.current[activeIndex].offsetTop)

    // remove some padding to make it look a lil better
    offset -= 25

    return offset
  }

  const calculateOffsetOnScroll = () => {
    const offsets = []
    $imageContainers.current.forEach(($el, i) => {
      if (!$el) return

      const boundingRect = $el.getBoundingClientRect()

      offsets[i] = {
        top: boundingRect.top,
        left: boundingRect.left,
        width: $el.offsetWidth,
        height: $el.offsetHeight,
      }
    })

    return offsets
  }

  useImperativeHandle(ref, () => ({
    getElement: () => {
      return $container
    },
    getOffsetByPercentScrolled,
    calculateOffsetOnScroll,
  }))

  useEffect(() => {
    const isGrid = productGalleryType === 'grid'
    gsap.killTweensOf($numberAndTitleContainers.current)
    gsap.to($numberAndTitleContainers.current, {
      opacity: isGrid ? 1 : 0,
      duration: 1,
      delay: 0.4,
      ease: 'power3.inOut',
    })
  }, [productGalleryType])

  return (
    <ul
      className={classnames(styles.ProductGalleryGridView, className)}
      ref={$container}
    >
      {products.map((product, i) => (
        <li
          key={i}
          data-orientation={product.imageOrientation}
          ref={ref => {
            $items.current[i] = ref
          }}
          className={classnames(styles.item, {
            [styles.notInFilter]:
              productGalleryActiveFilter && productGalleryActiveFilter !== product.productCategory.title,
          })}
          style={{
            '--bg-color': `var(--${colorsByIndex[i]})`,
          }}
        >
          <Link
            link={getProductLinkObject(product.slug.current, product.title)}
            className={styles.itemLink}
          >
            <div
              className={styles.itemImageContainer}
              data-index={i}
              data-box-type="grid"
              ref={ref => {
                $imageContainers.current[i] = ref
              }}
            >
              <SanityImage
                image={product.image}
                className={styles.image}
                priority
                breakpoints={PRODUCT_GALLERY_BREAKPOINTS}
                onLoad={e => {
                  gsap.killTweensOf(e.target)
                  gsap.to(e.target, {
                    opacity: 1,
                    duration: 0.6,
                    delay: Math.random() * 0.3,
                    ease: 'power3.inOut',
                  })
                }}
              />
            </div>
            <div
              className={styles.numberAndTitleContainer}
              ref={ref => {
                $numberAndTitleContainers.current[i] = ref
              }}
            >
              <p className={styles.number}>({formatNumber(i + 1, 2)})</p>
              <p className={styles.title}>{product.title}</p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
})

ProductGalleryGridView.displayName = 'ProductGalleryGridView'

export default ProductGalleryGridView
